import {
  Box,
  Card,
  CardContent,
  Container,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Button,
  IconButton,
  ListItemSecondaryAction,
  Breadcrumbs,
  Link,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useProcessModules } from 'src/hooks/use-process-modules';
import { paths } from 'src/paths';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { ProcessModule } from 'src/API';

const ProcessModuleList = () => {
  const modules = useProcessModules() as ProcessModule[];
  const navigate = useNavigate();
  const { organizationId } = useParams();

  // Filter modules for the organization
  const filteredModules = modules.filter(
    (module) => Array.isArray(module.managerGroups) && module.managerGroups?.includes(organizationId || ''),
  );

  const handleModuleClick = (moduleId: string) => {
    navigate(paths.settings.formBuilder.moduleForms(organizationId || '', moduleId));
  };

  const handleManageFieldsClick = () => {
    navigate(paths.settings.formBuilder.fields(organizationId || ''));
  };

  const handleCreateModule = () => {
    navigate(paths.settings.formBuilder.moduleNew(organizationId || ''));
  };

  const handleEditModule = (moduleId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(paths.settings.formBuilder.moduleEdit(organizationId || '', moduleId));
  };

  return (
    <Box component="main" sx={{ flexGrow: 1, py: 8 }}>
      <Container maxWidth="xl">
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 3 }}>
          <Typography color="text.primary">Process Modules</Typography>
          <Link></Link>
        </Breadcrumbs>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Typography variant="h4">Process Modules</Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="contained" color="primary" onClick={handleManageFieldsClick}>
              Manage Fields
            </Button>
            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleCreateModule}>
              New Process Module
            </Button>
          </Box>
        </Box>
        <Card>
          <CardContent>
            {filteredModules.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 300,
                  py: 6,
                  backgroundColor: '#f9fafb',
                  borderRadius: 2,
                }}
              >
                <Typography variant="h5" color="text.primary" align="center" gutterBottom>
                  No Process Modules Found
                </Typography>
                <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 3 }}>
                  You have not created any process modules yet. Get started by creating your first process module.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={<AddIcon />}
                  onClick={handleCreateModule}
                  sx={{ minWidth: 200 }}
                >
                  Create Process Module
                </Button>
              </Box>
            ) : (
              <List>
                {filteredModules.map((module) => (
                  <ListItem key={module.id} disablePadding>
                    <ListItemButton onClick={() => handleModuleClick(module.id)}>
                      <ListItemText primary={module.name} secondary={module.description || 'No description'} />
                      <ListItemSecondaryAction>
                        <IconButton edge="end" aria-label="edit" onClick={(e) => handleEditModule(module.id, e)}>
                          <EditIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default ProcessModuleList;
